import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Award, Calendar, Clock, User, BookOpen, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const VerifyCertificate = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState(null);

  const csvCode = searchParams.get('csv');

  useEffect(() => {
    const verifyCertificate = async () => {
      if (!csvCode) {
        setError('No se proporcionó código de verificación');
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(`${API_URL}/courses/verify-certificate/${encodeURIComponent(csvCode)}`);
        setCertificate(response.data.certificate);
      } catch (err) {
        setError('Certificado no encontrado o código inválido');
      } finally {
        setLoading(false);
      }
    };

    verifyCertificate();
  }, [csvCode]);

  const getCertificateTypeName = (type) => {
    const types = {
      'desa_cv': 'DESA Comunidad Valenciana',
      'cfc': 'Formación Continuada (CFC)',
      'propio': 'Certificado Propio',
      'manual': 'Certificado Personalizado'
    };
    return types[type] || type;
  };

  const getRoleBadge = (role) => {
    const map = {
      alumno: { label: 'Certificado de alumno', personLabel: 'Alumno/a', cls: 'bg-blue-100 text-blue-700' },
      docente: { label: 'Certificado de docencia', personLabel: 'Docente', cls: 'bg-teal-100 text-teal-700' },
      director: { label: 'Certificado de dirección', personLabel: 'Director/a de la formación', cls: 'bg-indigo-100 text-indigo-700' },
    };
    return map[role] || map.alumno;
  };

  const roleBadge = certificate ? getRoleBadge(certificate.recipient_role) : {};

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89] mx-auto"></div>
          <p className="mt-4 text-slate-600">Verificando certificado...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src="/api/courses/signatures/logo_irta.jpg" 
            alt="IRTA" 
            className="h-16 mx-auto"
            onError={(e) => e.target.style.display = 'none'}
          />
          <h1 className="text-2xl font-bold text-[#004E89] mt-4">Verificación de Certificado</h1>
        </div>

        {error ? (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center gap-3 text-red-600">
                <XCircle className="w-12 h-12" />
                <div>
                  <h2 className="text-xl font-bold">Certificado No Válido</h2>
                  <p className="text-sm">{error}</p>
                </div>
              </div>
              <p className="text-center mt-4 text-sm text-red-500">
                El código de verificación proporcionado no corresponde a ningún certificado registrado en nuestro sistema.
              </p>
            </CardContent>
          </Card>
        ) : certificate ? (
          <Card className="border-green-200 shadow-lg">
            <CardHeader className="bg-green-50 border-b border-green-100">
              <div className="flex items-center justify-center gap-3 text-green-600">
                <CheckCircle className="w-10 h-10" />
                <CardTitle className="text-xl">Certificado Válido</CardTitle>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              {/* Tipo de certificado */}
              <div className="flex items-center gap-2 justify-center flex-wrap">
                <Award className="w-5 h-5 text-[#004E89]" />
                <span className="font-medium text-[#004E89]">
                  {getCertificateTypeName(certificate.certificate_type)}
                </span>
                <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${roleBadge.cls}`} data-testid="cert-role-badge">
                  {roleBadge.label}
                </span>
              </div>

              {/* Datos del destinatario */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-500">{roleBadge.personLabel}</span>
                </div>
                <p className="text-lg font-semibold text-slate-800">{certificate.student_name}</p>
              </div>

              {/* Datos del curso */}
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <BookOpen className="w-4 h-4 text-slate-500" />
                  <span className="text-sm text-slate-500">Formación</span>
                </div>
                <p className="text-lg font-semibold text-slate-800">{certificate.course_name}</p>
              </div>

              {/* Detalles */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span className="text-xs text-slate-500">Fechas</span>
                  </div>
                  <p className="text-sm font-medium">{certificate.course_dates}</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-slate-500" />
                    <span className="text-xs text-slate-500">{(certificate.recipient_role === 'docente' || certificate.recipient_role === 'director') ? 'Horas impartidas' : 'Duración'}</span>
                  </div>
                  <p className="text-sm font-medium">{certificate.course_hours} horas</p>
                </div>
              </div>

              {/* Instructor */}
              {certificate.instructor_name && (
                <div className="text-center text-sm text-slate-600">
                  <span className="font-medium">Instructor:</span> {certificate.instructor_name}
                </div>
              )}

              {/* Créditos CFC */}
              {certificate.cfc_credits && (
                <div className="text-center text-sm text-[#004E89] font-medium">
                  Acreditado con {certificate.cfc_credits} créditos CFC
                </div>
              )}

              {/* Fecha de renovación (DESA) */}
              {certificate.renewal_date && (
                <div className="flex items-center justify-center gap-2 text-sm text-amber-600">
                  <RefreshCw className="w-4 h-4" />
                  <span>Renovación recomendada: {certificate.renewal_date}</span>
                </div>
              )}

              {/* Fecha de emisión */}
              <div className="text-center text-xs text-slate-400 pt-4 border-t">
                Certificado emitido el {certificate.issue_date}
              </div>

              {/* CSV */}
              <div className="text-center text-xs text-slate-400 font-mono">
                CSV: {csvCode}
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-slate-500">
          <p>IRTA Asistencia Médica Integral, SL.</p>
          <p className="text-xs mt-1">Sistema de verificación de certificados</p>
        </div>
      </div>
    </div>
  );
};

export default VerifyCertificate;
